import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, IntlProvider } from 'react-intl'
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles'

import { RootState } from './Store'
import { authActions } from './actions/AuthAction'
import { generalActions } from './actions/GeneralActions'
import { hydroModelActions } from './actions/HydroModelActions'
import { wikiActions } from './actions/WikiActions'
import { AuthState } from './reducers/AuthReducer'
import { WikiView } from './reducers/WikiReducer'

import messages_da from './translations/da.json'
import messages_en from './translations/en.json'

import { DataTheme } from './Enums'
import { getThemeOptionsByDataTheme } from './Themes'
import { LayerType } from './dataSources/layers/LayerRegistry'

import { appStyle } from './App.style'

import BottomBar from './components/bottombar/BottomBar'
import LeftSideBar from './components/leftsidebar/LeftSideBar'
import LoginDialog from './components/logindialog/LoginDialog'
import MainContentView from './components/maincontentview/MainContentView'
import RightSideBar from './components/rightsidebar/RightSideBar'
import ThemePicker from './components/themepicker/ThemePicker'
import ToolBar from './components/toolbar/ToolBar'
import TopBar from './components/topbar/TopBar'
import WikiSideBar from './components/wiki/wikisidebar/WikiSideBar'
import RedirectRoute from './components/redirectroute/RedirectRoute'
import { resetSelectedLayers } from './utils/MapLayerUtils'

// Set up localization
const locale = 'da'
const messages = {
  da: messages_da,
  en: messages_en
}

const tokenExpirationPadding = 3600000 // One hour
const checkTokenValidityInterval = 5000 // every 5 seconds

const App: FC = () => {
  const classes = appStyle()
  const dispatch = useDispatch()
  const [themePickerOpen, setThemePickerOpen] = useState(true)

  const {
    isLoggedIn,
    tokenExpirationTimestamp,
    username,
    password
  } = useSelector((state: RootState): AuthState => state.authState)
  const currentDataTheme: DataTheme = useSelector((state: RootState): DataTheme => state.generalState.dataTheme)
  const showLeftSideBar = useSelector((state: RootState): boolean => state.generalState.showLeftSideBar)
  const enabledLayers = useSelector((state: RootState): LayerType[] => state.mapState.enabledLayers)
  const currentWikiViews = useSelector((state: RootState): WikiView[] => state.wikiState.currentWikiViews)

  const handleClose = (): void => {
    setThemePickerOpen(false)
  }
  const handleThemeSelected = (dataTheme: DataTheme): void => {
    if (dataTheme !== currentDataTheme) {
      resetSelectedLayers(enabledLayers, dispatch)
    }
    dispatch(generalActions.setDataTheme(dataTheme))
    setThemePickerOpen(false)
  }

  /**
   * Handle the expiration of the GEO token.
   * Perform AUTOMATIC login/refresh token if username and password is available in memory.
   * Otherwise set the token to expired and trigger MANUAL login.
   */
  const handleExpiredGEOToken = (): void => {
    if (username === '' || password === '') {
      dispatch(authActions.setTokenExpired())
    } else if (password !== 'using-accesstoken') {
      dispatch(authActions.login({username: username, password: password}))
    }
  }

  /**
   * Checks if GEO token is valid.
   * Handle the event where the token is invalid or about to expire
   */
  const checkGEOToken = (): void => {
    if (tokenExpirationTimestamp === undefined) {
      handleExpiredGEOToken()
    } else {
      const nowMilli = new Date().getTime()
      const timeToExpiration = tokenExpirationTimestamp - nowMilli
      if (timeToExpiration < tokenExpirationPadding) {
        handleExpiredGEOToken()
      }
    }
  }

  useEffect(() => {
    checkGEOToken()
    const interval = setInterval(() => {
      checkGEOToken()
    }, checkTokenValidityInterval)
    return () => clearInterval(interval)
    // eslint-disable-next-line
  }, [tokenExpirationTimestamp, username, password])

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(wikiActions.fetchDocumentsMeta())
      dispatch(hydroModelActions.fetchHydroModels())
    }
    // eslint-disable-next-line
  }, [isLoggedIn])

  return (
    <IntlProvider locale={locale} key={locale} messages={messages[locale]}>
      <RedirectRoute/>
      <ThemeProvider theme={createMuiTheme(getThemeOptionsByDataTheme(currentDataTheme))}>
        <LoginDialog open />
        {isLoggedIn && currentDataTheme === DataTheme.NONE &&
          <ThemePicker open={themePickerOpen} handleThemeSelected={handleThemeSelected} handleClose={handleClose} />}
        <div className={classes.mobileContainer}>
          <div className={classes.topBar}>
            <TopBar />
          </div>
          <div className={classes.mainArea}>
            <div className={classes.mobileWarning}>
              <FormattedMessage id='mobile.warning' />
            </div>
          </div>
        </div>
        <div className={classes.mainContainer}>
          <div className={classes.topBar}>
            <TopBar />
          </div>
          <div className={classes.toolbar}>
            <ToolBar />
          </div>
          <div className={classes.mainArea}>
            <div className={classes.mainContentArea}>
              <MainContentView />
            </div>
            <div className={classes.leftSideBar}>
              <LeftSideBar open={showLeftSideBar} />
            </div>
            <div className={classes.rightSideBar}>
              <RightSideBar />
            </div>
            <div className={classes.wikiSideBar}>
              {
                currentWikiViews.map((view, index) => {
                  return (
                    <WikiSideBar
                      key={index}
                      navigationIndex={index}
                      viewType={view}
                    />
                  )
                })
              }
            </div>
            <div className={classes.bottomBar}>
              <BottomBar open={false} />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </IntlProvider>
  )
}

export default App
